import { Notifier } from '@/lib/notifier'

/* Port of AppStateNotifier (flutter_flow/nav/nav.dart) — the app-shell store
 * that gates every route behind the splash + auth resolution.
 *
 * AuthUser mirrors H2HWebAppFirebaseUser: a NON-NULL wrapper around a
 * possibly-null Firebase user. After the first auth emission `user` is never
 * null again — `loading` then depends only on the splash flag. */

export interface AuthUser {
  /** null uid == signed out (wrapper present, no Firebase user inside). */
  uid: string | null
  loggedIn: boolean
}

class ShellStore extends Notifier {
  initialUser: AuthUser | null = null
  user: AuthUser | null = null
  showSplashImage = true

  /* One-shot mute. prepareAuthEvent() sets it false so the sign-in emission
   * during Tracking's post-frame callback does NOT rebuild the router and
   * unmount the page mid-await. update() always re-arms it. */
  notifyOnAuthChange = true

  get loading(): boolean {
    return this.user == null || this.showSplashImage
  }

  get loggedIn(): boolean {
    return this.user?.loggedIn ?? false
  }

  /* Verbatim port of AppStateNotifier.update():
   *  - shouldUpdate: only when the uid actually changed (either side null
   *    counts as changed);
   *  - initialUser ??= newUser;
   *  - notify only if notifyOnAuthChange && shouldUpdate;
   *  - ALWAYS re-arm notifyOnAuthChange afterwards. */
  update(newUser: AuthUser): void {
    const shouldUpdate =
      this.user?.uid == null ||
      newUser.uid == null ||
      this.user?.uid !== newUser.uid

    this.initialUser ??= newUser
    this.user = newUser

    if (this.notifyOnAuthChange && shouldUpdate) {
      this.notifyListeners()
    }
    this.notifyOnAuthChange = true
  }

  /** GoRouter.prepareAuthEvent() equivalent. */
  prepareAuthEvent(): void {
    this.notifyOnAuthChange = false
  }

  stopShowingSplashImage(): void {
    this.showSplashImage = false
    this.notifyListeners()
  }
}

export const shellStore = new ShellStore()
